import { useEffect, useMemo, useState } from 'react';
import {
  departmentLabel,
  errorMessage,
  executionTone,
  financeGet,
  num,
  percent,
  won,
  type FinanceCtx,
} from '../financeApi';

type Line = { type: '수입' | '지출'; department: string; category: string; amount?: number; total?: number };
type Execution = {
  year: number;
  status: 'draft' | 'confirmed';
  throughMonth: number;
  items: Line[];
  actuals: Line[];
};

type Row = { label: string; budget: number; actual: number };

const TONE_LABEL = { none: '예산 없음', ok: '정상', warn: '⚠ 주의(90%↑)', over: '⛔ 초과' } as const;

function ExecutionTable({ rows, pace, labelHeader, income }: { rows: Row[]; pace: number; labelHeader: string; income?: boolean }) {
  const total = rows.reduce((acc, r) => ({ budget: acc.budget + r.budget, actual: acc.actual + r.actual }), { budget: 0, actual: 0 });
  return (
    <div className="manage-table-wrap">
      <table className="manage-table">
        <thead>
          <tr>
            <th>{labelHeader}</th>
            <th className="fin-num" style={{ width: 130 }}>{income ? '목표' : '예산'}</th>
            <th className="fin-num" style={{ width: 130 }}>{income ? '실적' : '집행'}</th>
            <th className="fin-num" style={{ width: 130 }}>{income ? '차이' : '잔액'}</th>
            <th style={{ width: 240 }}>{income ? '달성률' : '집행률'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="manage-table-empty">예산과 실적이 없습니다.</td></tr>}
          {rows.map((r) => {
            const rate = percent(r.actual, r.budget);
            const tone = income ? (r.budget > 0 ? 'ok' : 'none') : executionTone(rate, r.budget > 0);
            return (
              <tr key={r.label}>
                <td><b>{r.label}</b></td>
                <td className="fin-num">{r.budget ? num(r.budget) : '-'}</td>
                <td className="fin-num">{num(r.actual)}</td>
                <td className={`fin-num${!income && r.budget - r.actual < 0 ? ' manage-amt-out' : ''}`}>
                  {r.budget ? num(r.budget - r.actual) : '-'}
                </td>
                <td>
                  {r.budget > 0 ? (
                    <div className="fin-exec">
                      <div className="fin-meter" title={`연중 경과 ${pace}%`}>
                        <div className={`fin-meter-fill is-${tone}`} style={{ width: `${Math.min(100, rate)}%` }} />
                        <div className="fin-meter-pace" style={{ left: `${pace}%` }} />
                      </div>
                      <span>{rate}%</span>
                      {!income && tone !== 'ok' && <span className={`fin-tone is-${tone}`}>{TONE_LABEL[tone]}</span>}
                    </div>
                  ) : (
                    <span className="fin-muted">{TONE_LABEL.none}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <th>합계</th>
              <td className="fin-num"><b>{num(total.budget)}</b></td>
              <td className="fin-num"><b>{num(total.actual)}</b></td>
              <td className="fin-num"><b>{num(total.budget - total.actual)}</b></td>
              <td><b>{percent(total.actual, total.budget)}%</b></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function group(items: Line[], actuals: Line[], type: '수입' | '지출', keyOf: (l: Line) => string): Row[] {
  const map = new Map<string, Row>();
  const touch = (label: string) => map.get(label) ?? { label, budget: 0, actual: 0 };
  items.filter((i) => i.type === type).forEach((i) => {
    const row = touch(keyOf(i));
    row.budget += i.amount ?? 0;
    map.set(row.label, row);
  });
  actuals.filter((a) => a.type === type).forEach((a) => {
    const row = touch(keyOf(a));
    row.actual += a.total ?? 0;
    map.set(row.label, row);
  });
  return [...map.values()].sort((a, b) => b.budget - a.budget || b.actual - a.actual);
}

// 예산 대비 집행: 계정과목별·부서별 집행률(90% 이상 주의, 100% 초과 경고)과 수입 목표 달성률.
// 막대 위 세로선은 연중 경과율(선택한 월까지) - 그보다 많이 앞서 있으면 집행이 빠른 것.
export default function BudgetExecution({ ctx }: { ctx: FinanceCtx }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [throughMonth, setThroughMonth] = useState(new Date().getMonth() + 1);
  const [view, setView] = useState<'category' | 'department' | 'detail' | 'income'>('category');
  const [data, setData] = useState<Execution | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    financeGet<Execution>(ctx, '/budget-execution', { year, throughMonth })
      .then(setData)
      .catch((err) => setError(errorMessage(err, '집행 현황을 불러오지 못했습니다.')));
  }, [ctx, year, throughMonth]);

  const pace = Math.round((throughMonth / 12) * 100);

  const views = useMemo(() => {
    if (!data) return null;
    return {
      category: group(data.items, data.actuals, '지출', (l) => l.category),
      department: group(data.items, data.actuals, '지출', (l) => departmentLabel(l.department)),
      detail: group(data.items, data.actuals, '지출', (l) => `${departmentLabel(l.department)} · ${l.category}`),
      income: group(data.items, data.actuals, '수입', (l) => l.category),
    };
  }, [data]);

  const alerts = useMemo(() => {
    if (!views) return [];
    return views.detail
      .map((r) => ({ ...r, rate: percent(r.actual, r.budget) }))
      .filter((r) => r.budget > 0 && r.rate >= 90)
      .sort((a, b) => b.rate - a.rate);
  }, [views]);

  const expenseTotal = views?.category.reduce((acc, r) => ({ budget: acc.budget + r.budget, actual: acc.actual + r.actual }), { budget: 0, actual: 0 });
  const incomeTotal = views?.income.reduce((acc, r) => ({ budget: acc.budget + r.budget, actual: acc.actual + r.actual }), { budget: 0, actual: 0 });
  const unbudgeted = views?.detail.filter((r) => r.budget === 0 && r.actual > 0) ?? [];

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">예산 대비 집행</h2>
          <p className="manage-panel-desc">연간 예산과 실제 집행을 비교합니다. 집행률 90% 이상은 주의, 100% 초과는 경고로 표시됩니다.</p>
        </div>
        <div className="fin-inline">
          <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {Array.from({ length: 6 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select className="fin-select" value={throughMonth} onChange={(e) => setThroughMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>1~{m}월</option>)}
          </select>
        </div>
      </div>

      {error && <div className="manage-state manage-state--error">{error}</div>}
      {!error && !data && <div className="manage-state">불러오는 중입니다...</div>}

      {data && views && expenseTotal && incomeTotal && (
        <>
          {data.items.length === 0 && (
            <div className="fin-inline-error">{year}년 예산이 편성되지 않았습니다. '연간 예산 편성'에서 먼저 예산을 입력해 주세요.</div>
          )}
          {data.items.length > 0 && data.status !== 'confirmed' && (
            <div className="fin-inline-warn">{year}년 예산은 아직 확정되지 않았습니다(편성 중).</div>
          )}

          <div className="fin-kpis">
            <div className="fin-kpi"><span>지출 예산</span><strong>{won(expenseTotal.budget)}</strong></div>
            <div className="fin-kpi"><span>집행액 (1~{throughMonth}월)</span><strong className="manage-amt-out">{won(expenseTotal.actual)}</strong>
              <span className="fin-delta">연중 경과 {pace}%</span></div>
            <div className="fin-kpi fin-kpi--highlight"><span>집행률</span><strong>{percent(expenseTotal.actual, expenseTotal.budget)}%</strong>
              <span className="fin-delta">잔여 {won(expenseTotal.budget - expenseTotal.actual)}</span></div>
            <div className="fin-kpi"><span>수입 목표 달성률</span><strong>{percent(incomeTotal.actual, incomeTotal.budget)}%</strong>
              <span className="fin-delta">{num(incomeTotal.actual)} / {num(incomeTotal.budget)}원</span></div>
          </div>

          {alerts.length > 0 && (
            <div className="fin-alerts">
              <b>예산 경고</b>
              {alerts.map((a) => (
                <span key={a.label} className={`fin-tone is-${a.rate > 100 ? 'over' : 'warn'}`}>
                  {a.rate > 100 ? '⛔' : '⚠'} {a.label} {a.rate}% ({num(a.actual)} / {num(a.budget)})
                </span>
              ))}
            </div>
          )}
          {unbudgeted.length > 0 && (
            <div className="fin-inline-warn">
              예산 없이 집행된 항목: {unbudgeted.map((u) => `${u.label} ${num(u.actual)}원`).join(', ')}
            </div>
          )}

          <div className="manage-filterbar">
            <div className="manage-seg">
              {([
                ['category', '계정과목별'],
                ['department', '부서별'],
                ['detail', '부서 × 계정과목'],
                ['income', '수입 목표'],
              ] as const).map(([key, label]) => (
                <button key={key} type="button" className={`manage-seg-btn${view === key ? ' is-active' : ''}`} onClick={() => setView(key)}>{label}</button>
              ))}
            </div>
          </div>

          <div className="manage-card-block">
            <ExecutionTable
              rows={views[view]}
              pace={pace}
              income={view === 'income'}
              labelHeader={{ category: '계정과목', department: '부서', detail: '부서 · 계정과목', income: '헌금 항목' }[view]}
            />
          </div>
        </>
      )}
    </div>
  );
}
